/**
 * The site in the reader's language.
 *
 * Two catalogues per language, because they belong to different owners: `site` is this page's own
 * chrome, and `creator` is a partial override for `@project/event-creator-web`, which ships
 * English and takes the rest as a prop. Both live here rather than in the component package for
 * the reason stated there — an embed on someone else's page should not carry thirteen languages
 * it will never render.
 *
 * Only one language is ever in the bundle a reader downloads. Each catalogue is its own module
 * behind a `import()`, so Vite emits thirteen small chunks and the page fetches the one that was
 * chosen; English is the exception, since it is what the prerendered HTML already says.
 *
 * The selected catalogue owns all reusable website chrome as well as the editor. Long-form page
 * copy remains page data (see `pages.tsx`); localized variants can be added there without making
 * the embeddable editor carry site copy.
 */

import { EN_UNSAVED, type UnsavedMessages } from '../UnsavedDialog';
import type { ExampleTitles } from '../funnel-examples';
import {
  EN_PREVIEW,
  type WeekPreviewMessages,
  type EventCreatorMessages,
} from '@project/event-creator-web';
import { EN_WORKFLOW, type WorkflowMessages } from './workflow';
import type { PageTranslation } from '../pages';

export interface SiteMessages {
  readonly unsavedDialog: UnsavedMessages;
  readonly exampleTitles: ExampleTitles;
  readonly workflow: WorkflowMessages;
  readonly preview: WeekPreviewMessages;
  /** The two ways in. */
  readonly tabsLabel: string;
  readonly inlineEdit: {
    readonly heading: string;
    readonly unsaved: string;
    readonly cancel: string;
    readonly updated: string;
    readonly restoredEdit: string;
    readonly restoredDraft: string;
  };
  readonly entryHints?: { readonly form: string; readonly funnel: string };
  readonly tabForm: string;
  readonly tabFunnel: string;

  /** The form tab, when it is editing a row from the list rather than building something new. */
  readonly editingNote: string;
  readonly unsavedChanges: string;
  readonly editingStartNew: string;
  readonly addToList: string;
  readonly saveChanges: string;

  /** The funnel tab. */
  readonly funnelLabel: string;
  readonly funnelPlaceholder: string;
  readonly funnelBusy: string;
  readonly funnelAction: string;
  readonly funnelImageNote: string;
  readonly funnelFailed: string;

  /** The collected list. `{count}` and `{title}` fill in. */
  readonly listUntitled: string;
  readonly listAllDay: string;
  readonly listEditing: string;
  readonly listEdit: string;
  readonly listDownloadOne: string;
  readonly listRemove: string;
  readonly listRemoveLabel: string;
  readonly listDownloadOnlyOne: string;
  readonly listDownloadAll: string;
  /** Flags, by the `kind` the intake domain stamps rather than by its English note. */
  readonly flagLowConfidence: string;
  readonly flagDuplicateBatch: string;
  readonly flagDuplicateCalendar: string;

  /** The language control itself. */
  readonly languageLabel: string;

  /** Website chrome around the editor. */
  readonly questionsHeading: string;
  readonly pagesLabel: string;
  readonly privacyNotice: string;
  readonly creatorPlaceholder: string;
  readonly creatorNoScript: string;
  /** First-use guidance is required in every supported locale. */
  readonly quickStart: {
    readonly heading: string;
    readonly steps: readonly string[];
    readonly downloadHelp: string;
  };
}

export interface Language {
  readonly site: SiteMessages;
  /** Partial: anything missing falls back to the component's English. */
  readonly creator: Partial<EventCreatorMessages>;
  /** Long-form website pages, keyed by their slug (`home` represents `/`). */
  readonly pages?: Readonly<Record<string, PageTranslation>>;
}

export const EN_SITE: SiteMessages = {
  unsavedDialog: EN_UNSAVED,
  inlineEdit: {
    heading: 'Edit: {title}',
    unsaved: 'Unsaved changes',
    cancel: 'Cancel editing',
    updated: '{title} updated.',
    restoredEdit: 'Draft restored — changes haven’t been saved to your event yet.',
    restoredDraft: 'Your draft has been restored.',
  },
  exampleTitles: {
    coffee: ['Coffee with Maya', 'Lunch with Alex'],
    design: ['Design workshop', 'Project demo'],
    books: ['Book club', 'Poetry night'],
    exercise: ['Morning yoga', 'Evening swim'],
    art: ['Pottery class', 'Watercolor workshop'],
    garden: ['Picnic in the park', 'Garden party'],
    culture: ['Museum visit', 'Outdoor cinema'],
    travel: ['Train to the coast', 'Dinner by the sea'],
    music: ['Guitar lesson', 'Choir rehearsal'],
    community: ['Community cleanup', 'Volunteer welcome'],
    cooking: ['Cooking class', 'Board game night'],
    winter: ['Winter market', 'Family dinner'],
  },
  workflow: EN_WORKFLOW,
  preview: EN_PREVIEW,
  tabsLabel: 'How to add an event',
  tabForm: 'Create an event',
  tabFunnel: 'Import a schedule',
  entryHints: { form: 'Type the details yourself', funnel: 'Use text, a photo or a file' },

  editingNote: 'Editing an event from the list.',
  unsavedChanges: 'Save your changes before downloading the calendar.',
  editingStartNew: 'Create another event',
  addToList: 'Save to event list',
  saveChanges: 'Save changes',

  funnelLabel: 'Paste a schedule, or attach one',
  funnelPlaceholder: 'Book club, 12 September 2027, 7–8pm\nTeam lunch, 15 September 2027, 12–1pm',
  funnelBusy: 'Reading…',
  funnelAction: 'Find events',
  funnelImageNote:
    'Reading a picture happens here, on this machine — which means downloading the recogniser the first time, about 6 MB. Nothing about the picture is uploaded.',
  funnelFailed: 'Something went wrong reading that. Try again, or type it instead.',

  listUntitled: 'Untitled event',
  listAllDay: 'all day',
  listEditing: 'Editing on the form tab',
  listEdit: 'Edit',
  listDownloadOne: 'Download event',
  listRemove: 'Remove',
  listRemoveLabel: 'Remove {title}',
  listDownloadOnlyOne: 'Download calendar file (.ics)',
  listDownloadAll: 'Download all {count} as one file',
  flagLowConfidence: "I wasn't sure I read this one right",
  flagDuplicateBatch: 'Looks like another event in this batch — “{title}”',
  flagDuplicateCalendar: 'Looks like an event already on your calendar — “{title}”',

  languageLabel: 'Language',
  questionsHeading: 'Questions',
  pagesLabel: 'Pages',
  privacyNotice: 'Events are built locally. Nothing you type here is sent anywhere.',
  creatorPlaceholder:
    'Title, location, date and time, time zone, repeat, invitees, alerts, and notes — then download the .ics file or open it straight in your calendar. You can also paste a schedule or attach a document or photo to make several events at once.',
  quickStart: {
    heading: 'How it works',
    steps: [
      'Enter an event name, date and time. Everything else is optional.',
      'Check the details, then download your calendar file.',
      'Open or import the file in your calendar app to add the event.',
    ],
    downloadHelp:
      'Downloading creates a file; it does not add the event to your calendar yet. Open the .ics file with Apple Calendar or Outlook, or import it using Google Calendar on a computer.',
  },
  creatorNoScript:
    'The builder needs JavaScript, because building the file in your browser is what keeps the event off anyone else’s server.',
};

/** The language a page opens in, and the one the prerendered HTML is built with. */
export const DEFAULT_LANGUAGE = 'en';

export const EN: Language = {
  site: EN_SITE,
  creator: { downloadEvent: 'Download calendar file', groupDownload: 'Review and download' },
};

export type LanguageTag = (typeof SITE_LANGUAGES)[number][0];

export function isLanguage(tag: string): tag is LanguageTag {
  return SITE_LANGUAGES.some(([candidate]) => candidate === tag);
}

/**
 * The offered languages, named in themselves — someone looking for their own language is looking
 * for the word they call it, not for our English name for it.
 *
 * One entry per language, with Chinese the single exception: Simplified and Traditional differ
 * for 235 of the 280 regions `Intl` can name, where `en-GB` differs from `en` for nine and
 * `pt-BR` from `pt` for none. A regional variant that changes nothing on screen is a menu entry
 * that wastes a reader's time, and worse, implies the dates changed with it — they follow the
 * reader's own browser, here as everywhere.
 *
 * Left-to-right only for now: nothing on this page flips, and an Arabic or Hebrew interface
 * needs a page-wide `dir`, not a list entry.
 */
export const SITE_LANGUAGES: readonly (readonly [string, string])[] = [
  ['en', 'English'],
  ['de', 'Deutsch'],
  ['es', 'Español'],
  ['fr', 'Français'],
  ['it', 'Italiano'],
  ['nl', 'Nederlands'],
  ['pl', 'Polski'],
  ['pt', 'Português'],
  ['tr', 'Türkçe'],
  ['ru', 'Русский'],
  ['ja', '日本語'],
  ['ko', '한국어'],
  ['zh-Hans', '简体中文'],
  ['zh-Hant', '繁體中文'],
];

/**
 * One `import()` per language, written out rather than built from a template string: a bundler
 * can only split what it can see, and `import(`./${tag}`)` is a runtime string that Vite would
 * either give up on or resolve by shipping every catalogue to every reader.
 */
const CATALOGUES: Readonly<Record<string, () => Promise<{ default: Language }>>> = {
  de: () => import('./de'),
  es: () => import('./es'),
  fr: () => import('./fr'),
  it: () => import('./it'),
  nl: () => import('./nl'),
  pl: () => import('./pl'),
  pt: () => import('./pt'),
  tr: () => import('./tr'),
  ru: () => import('./ru'),
  ja: () => import('./ja'),
  ko: () => import('./ko'),
  'zh-Hans': () => import('./zh-Hans'),
  'zh-Hant': () => import('./zh-Hant'),
};

/** The catalogue for a tag, or English — including when the chunk fails to arrive. */
export async function loadLanguage(tag: string): Promise<Language> {
  const load = CATALOGUES[tag];
  if (load === undefined) return EN;
  try {
    return (await load()).default;
  } catch {
    // A chunk that 404s after a deploy must not take the page down with it: the reader gets
    // English, which is what the HTML they are looking at already says.
    return EN;
  }
}

/** Where the choice is kept between pages. Every page here is its own document. */
const STORAGE_KEY = 'ics-maker-language';

/**
 * The remembered choice, or the default. Checked against the list rather than trusted: what comes
 * back is whatever is in that browser's storage, which is a string anyone can set.
 */
export function storedLanguage(): string {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored !== null && SITE_LANGUAGES.some(([tag]) => tag === stored)
      ? stored
      : DEFAULT_LANGUAGE;
  } catch {
    return DEFAULT_LANGUAGE;
  }
}

/** Remembers the choice for the next page. Failing to is not worth an error on this page. */
export function rememberLanguage(tag: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, tag);
  } catch {
    // Storage disabled, or a private window that refuses it: the choice still applies here.
  }
}

/** `{name}` → value, for the handful of site strings that carry one. */
export function fillSite(
  template: string,
  values: Readonly<Record<string, string | number>>,
): string {
  return template.replace(/\{(\w+)\}/g, (whole, name: string) =>
    name in values ? String(values[name]) : whole,
  );
}
